import { useQuery } from '@tanstack/react-query';
import type { DisplayBoardView } from '@wodi/shared';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

import { ApiError } from '../api/client.ts';
import { claimDisplay, fetchDisplayBoard } from '../api/displays.ts';
import { QUEUE_REFRESH_MS } from '../api/triage.ts';
import styles from '../triage/queue-display.module.css';

// The waiting-area display (docs/plans/phase1-triage.md §6.2; ADR 0013). Full screen, no
// app shell, ticket numbers and where to go only. It pairs from the link's fragment
// (never sent to a server), then polls; if the server is unreachable for 30 seconds it
// says so instead of showing stale numbers.

const STALE_AFTER_MS = 30_000;

type Pairing = 'checking' | 'paired' | 'failed';

function tokenFromHash(hash: string): string | null {
  const match = /(?:^#?|&)token=([^&]+)/.exec(hash);
  return match?.[1] === undefined ? null : decodeURIComponent(match[1]);
}

export function QueueDisplayPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [initialToken] = useState(() => tokenFromHash(location.hash));
  const [pairing, setPairing] = useState<Pairing>(initialToken === null ? 'paired' : 'checking');

  useEffect(() => {
    if (initialToken === null) return;
    const token = initialToken;
    // Drop the token from the address at once, so it isn't left on screen or in history.
    void navigate({ to: '/display', hash: '', replace: true });
    claimDisplay(token).then(
      () => {
        setPairing('paired');
      },
      () => {
        setPairing('failed');
      },
    );
  }, [initialToken, navigate]);

  const board = useQuery({
    queryKey: ['display', 'board'],
    queryFn: fetchDisplayBoard,
    enabled: pairing === 'paired',
    refetchInterval: QUEUE_REFRESH_MS,
    refetchIntervalInBackground: true,
    retry: false,
  });

  const revoked =
    pairing === 'failed' ||
    (board.error instanceof ApiError && board.error.code === 'display_revoked');
  // Failing for longer than the limit since the last good answer (or never answering).
  const stale =
    board.isError &&
    !revoked &&
    (board.dataUpdatedAt === 0 || board.errorUpdatedAt - board.dataUpdatedAt > STALE_AFTER_MS);
  const data: DisplayBoardView | undefined = board.data;

  let body;
  if (revoked) {
    body = (
      <p className={styles.message}>
        This display has been disconnected. Pair it again from Settings.
      </p>
    );
  } else if (stale) {
    body = <p className={styles.message}>Display offline. Check the network.</p>;
  } else if (data === undefined) {
    body = <p className={styles.message}>Starting…</p>;
  } else {
    body = (
      <div className={styles.columns}>
        <section aria-labelledby="calling" className={styles.column}>
          <h2 id="calling" className={styles.columnTitle}>
            Now calling
          </h2>
          {data.calling.length === 0 ? (
            <p className={styles.quiet}>No one is being called.</p>
          ) : (
            <ul className={styles.calls}>
              {data.calling.map((c) => (
                <li key={`${c.ticket}-${c.servicePoint}`} className={styles.call}>
                  <span className={styles.ticket}>{c.ticket}</span>
                  <span className={styles.where}>{c.servicePoint}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
        <section aria-labelledby="waiting" className={styles.column}>
          <h2 id="waiting" className={styles.columnTitle}>
            Waiting
          </h2>
          {data.waiting.map((w) => (
            <div key={w.servicePoint} className={styles.queue}>
              <h3 className={styles.queueName}>{w.servicePoint}</h3>
              {w.tickets.length === 0 ? (
                <p className={styles.quiet}>None</p>
              ) : (
                <ul className={styles.tickets} aria-label={`Waiting for ${w.servicePoint}`}>
                  {w.tickets.map((t) => (
                    <li key={t}>{t}</li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </section>
      </div>
    );
  }

  return (
    <main className={styles.screen} aria-label={data?.displayName ?? 'Queue display'}>
      {body}
    </main>
  );
}
